import React, { useRef, useCallback, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { makeGetTask } from 'selectors/tasks';
import { makeGetMilestone } from 'selectors/milestones';

import { useCanUser } from 'shared-hooks/permission';

import RecursionRuleContainer from 'containers/recursion-rule-container';

import TaskDueDatePicker from 'components/TaskDueDatePicker';
import TaskStartDatePicker from 'components/TaskStartDatePicker';
import TaskReminderPicker from 'components/TaskReminderPicker';
import TaskRecursionPicker from 'components/TaskRecursionPicker';
import DateTime from 'components/DateTime';
import Tooltip from 'components/tooltip';
import Popout from 'components/popout';
import TaskDatesNav from 'components/TaskDatesNav';
import ResourceRepeatText from 'components/resource-repeat-text';

import IconCalendarSimple from 'icons/icon-calendar-simple';
import IconRepeat from 'icons/icon-repeat';
import IconActivity from 'icons/icon-activity';

import './TaskDates.styles.css';

const Tabs = ({ currentTab, taskId, showQuickOptions, onHide, provideController }) => {
    if (currentTab === 'dueDate') {
        return (
            <TaskDueDatePicker
                key="dueDate"
                id={taskId}
                inline={true}
                autoShow={true}
                withTitle={false}
                saveOnClose={false}
                showQuickOptions={showQuickOptions}
                onComplete={onHide}
                onCancel={onHide}
                provideController={provideController}
            />
        );
    }

    if (currentTab === 'startDate') {
        return (
            <TaskStartDatePicker
                key="startDate"
                id={taskId}
                inline={true}
                autoShow={true}
                withTitle={false}
                saveOnClose={false}
                showQuickOptions={showQuickOptions}
                onComplete={onHide}
                onCancel={onHide}
                provideController={provideController}
            />
        );
    }

    if (currentTab === 'reminder') {
        return (
            <TaskReminderPicker
                key="reminder"
                id={taskId}
                inline={true}
                autoShow={true}
                withTitle={false}
                saveOnClose={false}
                showQuickOptions={showQuickOptions}
                onComplete={onHide}
                onCancel={onHide}
                provideController={provideController}
            />
        );
    }

    if (currentTab === 'recurring') {
        return (
            <TaskRecursionPicker
                key="recurring"
                id={taskId}
                inline={true}
                autoShow={true}
                withTitle={false}
                onComplete={onHide}
                onCancel={onHide}
                provideController={provideController}
            />
        );
    }

    return null;
}

const TaskDates = ({ id, taskCompleted, taskCreatedBy, taskDueDate, taskStartDate, taskReminder, taskRule, showQuickOptions = true, xAlign = 'right', defaultXPosition = 'left' }) => {
    const popoutCtrl = useRef(null);
    const pickerCtrl = useRef(null);
    const canEdit = useCanUser('editTask', taskCreatedBy);
    const [currentTab, setCurrentTab] = useState('dueDate');
    const { t } = useTranslation(['task', 'common']);

    const handleClick = useCallback(() => {
        popoutCtrl.current?.show();
    }, [popoutCtrl]);

    const handleDueDateClick = useCallback(() => {
        setCurrentTab('dueDate');
        popoutCtrl.current?.show();
    }, [popoutCtrl]);

    const handleStartDateClick = useCallback(() => {
        setCurrentTab('startDate');
        popoutCtrl.current?.show();
    }, [popoutCtrl]);

    const handleRecurringClick = useCallback(() => {
        setCurrentTab('recurring');
        popoutCtrl.current?.show();
    }, [popoutCtrl]);

    const handleReminderClick = useCallback(() => {
        setCurrentTab('reminder');
        popoutCtrl.current?.show();
    }, [popoutCtrl]);

    const handleNavClick = useCallback(nextTab => {
        if (currentTab === nextTab) {
            return;
        }

        if (pickerCtrl.current?.save) {
            pickerCtrl.current.save();
        }

        setCurrentTab(nextTab);
    }, [currentTab, pickerCtrl]);

    const handleTabHide = useCallback(() => {
        if (!popoutCtrl.current) {
            return;
        }

        popoutCtrl.current.hide();
    }, [popoutCtrl]);

    const dueAndStartDateElement = useMemo(() => {
        if (!taskDueDate && !taskStartDate) {
            return null;
        }

        const isOverdue = moment().diff(taskDueDate) > 0 && !taskCompleted;

        if (!taskStartDate) {
            return (
                <Tooltip showDelay={200} withTooltipHover={false} placement="top" tooltip={(
                    <React.Fragment>
                        <DateTime format="fullDateTime">{taskDueDate}</DateTime><br />
                        <small><DateTime fromNow>{taskDueDate}</DateTime></small>
                    </React.Fragment>
                )}>
                    <div className={'task-date-item underlined-text-on-hover' + (isOverdue ? ' text-danger' : '')} onClick={handleDueDateClick}>
                        { t('common:Due') }:&nbsp;<DateTime format="fullDateTime">{taskDueDate}</DateTime>
                    </div>
                </Tooltip>
            );
        }

        if (!taskDueDate) {
            return (
                <Tooltip showDelay={200} withTooltipHover={false} placement="top" tooltip={(
                    <React.Fragment>
                        <DateTime format="fullDateTime">{taskStartDate}</DateTime><br />
                        <small><DateTime fromNow>{taskStartDate}</DateTime></small>
                    </React.Fragment>
                )}>
                    <div className="task-date-item underlined-text-on-hover" onClick={handleStartDateClick}>
                        { t('common:Start') }:&nbsp;<DateTime format="fullDateTime">{taskStartDate}</DateTime>
                    </div>
                </Tooltip>
            )
        }

        return (
            <Tooltip showDelay={200} withTooltipHover={false} placement="top" tooltip={(
                <React.Fragment>
                    { t('common:Start') }: <DateTime format="fullDateTime">{taskStartDate}</DateTime><br />
                    { t('common:Due') }: <DateTime format="fullDateTime">{taskDueDate}</DateTime>
                </React.Fragment>
            )}>
                <div className="task-date-item">
                    <span className="underlined-text-on-hover" onClick={handleStartDateClick}>
                        <DateTime format="date">{taskStartDate}</DateTime>
                    </span>

                    &nbsp;-&nbsp;

                    <span className={'underlined-text-on-hover' + (isOverdue ? ' text-danger' : '')} onClick={handleDueDateClick}>
                        <DateTime format="dateTimeShort">{taskDueDate}</DateTime>
                    </span>
                </div>
            </Tooltip>
        );
    }, [taskDueDate, taskStartDate, taskCompleted, handleDueDateClick, handleStartDateClick, t]);

    const reminderElement = useMemo(() => {
        if (!taskReminder) {
            return null;
        }

        return (
            <Tooltip showDelay={200} withTooltipHover={false} placement="top" tooltip={(
                <React.Fragment>
                    { t('common:Reminder') }:<br />
                    <DateTime format="fullDateTime">{taskReminder}</DateTime>
                </React.Fragment>
            )}>
                <div className="task-date-item underlined-text-on-hover" onClick={handleReminderClick}>
                    <span className="task-date-item-icon">
                        <IconActivity />
                    </span>
                    
                    <span className="task-date-item-text">
                        <DateTime format="dateTime">{taskReminder}</DateTime>
                    </span>
                </div>
            </Tooltip>
        );
    }, [taskReminder]);

    const recurringElement = useMemo(() => {
        if (!taskRule) {
            return null;
        }

        return (
            <Tooltip showDelay={200} withTooltipHover={false} placement="top" tooltip={ t('This task is recurring') }>
                <div className="task-date-item underlined-text-on-hover" onClick={handleRecurringClick}>
                    <span className="task-date-item-icon">
                        <IconRepeat />
                    </span>

                    <span className="task-date-item-text">
                        <RecursionRuleContainer id={taskRule}>
                            <ResourceRepeatText />
                        </RecursionRuleContainer>
                    </span>
                </div>
            </Tooltip>
        );
    }, [taskRule, handleRecurringClick, t]);

    return (
        <div className={'meta-item with-tooltip task-action task-dates' + (canEdit ? ' can-edit' : '')} onClick={handleClick}>

            { dueAndStartDateElement }

            { reminderElement }

            { recurringElement }

            {
                canEdit ? (
                    <React.Fragment>
                        {
                            !dueAndStartDateElement && !reminderElement && !recurringElement ? (
                                <span className="placeholder action-text">
                                    { t('common:Add') }
                                </span>
                            ) : null
                        }

                        <Popout
                            className="task-dates-popout dark-arrow"
                            xAlign={xAlign}
                            defaultXPosition={defaultXPosition}
                            provideController={popoutCtrl}
                            // onShow={this.props.onShow}
                            // onHide={this.props.onHide}
                        >
                            <TaskDatesNav current={currentTab} onClick={handleNavClick} />

                            <Tabs currentTab={currentTab} taskId={id} showQuickOptions={showQuickOptions} onHide={handleTabHide} provideController={pickerCtrl} />
                        </Popout>
                    </React.Fragment>
                ) : null
            }
        </div>
    );
};

const makeMapStateToProps = () => {
    const getTask = makeGetTask();
    const getMilestone = makeGetMilestone();

    return ({ tasks, milestones }, ownProps) => {
        const task = getTask(tasks.items, ownProps.id);

        return {
            taskCompleted: task?.completed,
            taskDueDate: task?.due_date,
            taskStartDate: task?.start_date,
            taskReminder: task?.reminder,
            taskRule: task?.rule,
            taskCreatedBy: task?.created_by
        }
    }
}

export default connect(makeMapStateToProps)(React.memo(TaskDates));
